import {
  filterPractitionerMembers,
  withStickySelectedMembers,
  type PractitionerMemberRef,
  type PractitionerRoleRef,
} from '@/lib/practitionerRole';

/** Journal write → Tidligere notater: show every active employee. */
export const JOURNAL_WRITE_ALL_EMPLOYEES = 'employees';

export type JournalWriteStaffMember = PractitionerMemberRef & {
  user_id: string;
  name: string;
};

export type JournalWriteAuthorRef = {
  clinician_id: string | null;
  author_name: string | null;
};

export function journalWritePractitionerChoices(input: {
  members: readonly JournalWriteStaffMember[];
  roles: readonly PractitionerRoleRef[];
  rolesReady: boolean;
  entries: readonly JournalWriteAuthorRef[];
  selectedId: string;
}): { id: string; name: string }[] {
  const showAllEmployees = !input.rolesReady || input.selectedId === JOURNAL_WRITE_ALL_EMPLOYEES;
  const practitioners = input.rolesReady
    ? filterPractitionerMembers([...input.members], [...input.roles])
    : [...input.members];
  const practitionerIds = new Set(practitioners.map((member) => member.user_id));
  const activeIds = new Set(input.members.map((member) => member.user_id));
  const listed = showAllEmployees
    ? [...input.members]
    : withStickySelectedMembers(practitioners, input.members, [input.selectedId]);

  const rows = new Map<string, string>();
  for (const member of listed) rows.set(member.user_id, member.name);
  for (const entry of input.entries) {
    if (!entry.clinician_id || !entry.author_name) continue;
    const activeNonPractitioner =
      activeIds.has(entry.clinician_id) && !practitionerIds.has(entry.clinician_id);
    if (!showAllEmployees && activeNonPractitioner && entry.clinician_id !== input.selectedId) {
      continue;
    }
    if (!rows.has(entry.clinician_id)) rows.set(entry.clinician_id, entry.author_name);
  }
  return [...rows.entries()].map(([id, name]) => ({ id, name }));
}

/**
 * Empty filter = Alle behandlere. Notes by an active member who is not flagged
 * are hidden. Former authors (not an active member) stay visible.
 * `employees` shows every note.
 */
export function journalWriteNoteMatchesPractitioner(input: {
  clinicianId: string | null;
  authorName: string | null;
  filterId: string;
  practitionerIds: ReadonlySet<string>;
  activeMemberIds: ReadonlySet<string>;
}): boolean {
  if (input.filterId === JOURNAL_WRITE_ALL_EMPLOYEES) return true;
  if (!input.filterId) {
    if (
      input.clinicianId &&
      input.activeMemberIds.has(input.clinicianId) &&
      !input.practitionerIds.has(input.clinicianId)
    ) {
      return false;
    }
    return true;
  }
  if (input.clinicianId === input.filterId) return true;
  return Boolean(input.authorName) && input.authorName === input.filterId;
}
